import { Outlet, createFileRoute, useMatchRoute, useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'
import { Permission } from '../../features/users/types'
import { isAuthorized } from '../../features/users/services'
import { useAppStore, useLocalStore } from '~/stores'
import { BannerPortal, Boundary } from '../../components'
import { WorkspaceUsageCallout } from '../../features/billing/components/WorkspaceUsageCallout'
import { UnpaidInvoiceCallout } from '../../features/billing/components/UnpaidInvoiceCallout'
import { LegacyPricingCallout } from '../../features/billing/components/LegacyPricingCallout'
import { useCurrentRouteId, useFeature, useIsAuthorized } from '~/hooks'
import { config } from '~/shared'
import { setTag } from '@sentry/react'
import { getQueryOptions } from '~/services'
import { AlertTitle, ColoredAlert } from '@bpinternal/ui-kit-next'
import { getWorkspacePreference } from '~/services/preferences/workspacePreferences'
import { initSupportWebchat } from '~/utils/support-webchat'
import { FileRouteTypes } from '~/routeTree.gen'

const BANNER_DISABLED_PAGES: FileRouteTypes['to'][] = [
  '/workspaces/$workspaceId/bots/$botId/new-bot',
  '/workspaces/$workspaceId/bots/$botId/new-chat',
]

export const Route = createFileRoute('/workspaces/$workspaceId')({
  beforeLoad: async ({ context, params }) => {
    initSupportWebchat(params.workspaceId)
    const members = await context.queryClient.ensureQueryData(
      getQueryOptions('workspaces_/$workspaceId_/members', { workspaceId: params.workspaceId })
    )
    const workspaceMember = members?.find((member) => member.userId === context.user.id)
    const isUserAuthorized = (permission: Permission): boolean | undefined => {
      if (!workspaceMember) {
        return undefined
      }

      return isAuthorized({ permission, role: workspaceMember?.role })
    }
    return { isUserAuthorized }
  },
  loader: async ({ context, params, location }) => {
    const { setActiveWorkspace } = useAppStore.getState()
    const { setLastActiveWorkspaceId } = useLocalStore.getState()
    const workspace = await context.queryClient.fetchQuery(
      getQueryOptions('workspaces_/$workspaceId_', { workspaceId: params.workspaceId })
    )
    setActiveWorkspace(workspace)
    setLastActiveWorkspaceId(workspace.id)

    const onboardingCompleted = await getWorkspacePreference(
      {
        path: 'onBoardingCompleted',
      },
      params.workspaceId
    )

    return {
      shouldRedirectToOnboarding:
        workspace.botCount === 0 &&
        !location.pathname.endsWith('/create-bot') &&
        !location.pathname.endsWith('/new-bot') &&
        !onboardingCompleted,
    }
  },
  component: Component,
})

function Component() {
  const { workspaceId } = Route.useParams()
  const { user } = Route.useRouteContext()
  const { shouldRedirectToOnboarding } = Route.useLoaderData()
  const hasAccess = useIsAuthorized({ workspaceId, userId: user.id })
  const isOnboardingEnabled = useFeature('dashboard-onboarding')
  const matchRoute = useMatchRoute()
  const routeId = useCurrentRouteId()
  const navigate = useNavigate()

  setTag('workspaceId', workspaceId)

  const isBannerEnabled = !BANNER_DISABLED_PAGES.some((route) => matchRoute({ to: route }))

  useEffect(() => {
    if (isOnboardingEnabled && shouldRedirectToOnboarding) {
      navigate({ to: '/workspaces/$workspaceId/create-bot', params: { workspaceId }, replace: true })
    }
  }, [isOnboardingEnabled, shouldRedirectToOnboarding, navigate, workspaceId])

  if (isOnboardingEnabled && shouldRedirectToOnboarding) {
    return null
  }

  function isWebchatLocation(): boolean {
    if (!routeId) return false

    return (
      routeId.includes('/webchat/v2/identity') ||
      routeId.includes('/webchat/v2/appearance') ||
      routeId.includes('/webchat/v2/deploy-settings') ||
      routeId.includes('/webchat/v2/features') ||
      routeId.includes('/webchat/v2/') ||
      routeId.includes('/webchat')
    )
  }

  return (
    <>
      {isBannerEnabled && (
        <BannerPortal>
          <Boundary loader={null} onError={() => null}>
            {hasAccess('workspace.manageBilling') && <UnpaidInvoiceCallout workspaceId={workspaceId} />}
            <WorkspaceUsageCallout workspaceId={workspaceId} className={isWebchatLocation() ? 'xl:w-[47.5%]' : ''} />
            <LegacyPricingCallout workspaceId={workspaceId} className={isWebchatLocation() ? 'xl:w-[47.5%]' : ''} />
          </Boundary>
          {!user.emailVerified && (
            <ColoredAlert color="blue" className={isWebchatLocation() ? 'xl:w-[47.5%]' : ''}>
              <AlertTitle>
                Your email address is not verified. Email verification will be enforced soon.{' '}
                <a className="underline" target="_blank" rel="noreferrer" href={`${config.ssoBaseUrl}/verification`}>
                  Verify now
                </a>
              </AlertTitle>
            </ColoredAlert>
          )}
        </BannerPortal>
      )}
      <Outlet />
    </>
  )
}
